import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Link2, Share2, X } from "lucide-react";
import { cleanName, makeShareCard, saveName, savedName, shareUrl } from "../../lib/shareCard";

const quiet =
  "flex items-center gap-2 rounded-full border border-[#7E57C2]/25 bg-white/70 px-4 py-2 font-display text-base text-[#3B2656] transition-colors duration-300 hover:bg-[#7E57C2] hover:text-white disabled:opacity-50";

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

export function ShareSheet({ painting, memory, onClose }) {
  const [name, setName] = useState(savedName);
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const who = cleanName(name);
  const link = shareUrl(painting.slug, who);

  useEffect(() => {
    let url = null;
    let live = true;
    const id = setTimeout(() => {
      makeShareCard(painting, memory, who)
        .then(({ blob, info }) => {
          if (!live) return;
          url = URL.createObjectURL(blob);
          setCard({ blob, url, info });
        })
        .catch(() => live && setError("The card couldn't be made just now."));
    }, card ? 350 : 0);
    return () => {
      live = false;
      clearTimeout(id);
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painting, memory, who]);

  const file = useMemo(() => card && new File([card.blob], `${painting.slug}-a-feeling.png`, { type: "image/png" }), [card, painting.slug]);
  const canNative = !!(file && navigator.canShare?.({ files: [file] }));

  const native = async () => {
    try {
      await navigator.share({ files: [file], title: painting.title, text: `${painting.title}, ${painting.emotion}. Feeling Heart · Rainbow by the Rain by Priyanka Jha`, url: link });
    } catch {
      setStatus("You can download the card or copy the link instead.");
    }
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = card.url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setStatus("Card saved");
  };
  const copy = async () => setStatus((await copyText(link)) ? "Link copied" : "Couldn't copy, the link is shown below");

  return (
    <motion.div
      data-testid="share-sheet"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center overflow-y-auto bg-[#2A1B3D]/30 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="relative flex w-full max-w-md flex-col items-center gap-4 rounded-3xl border border-white/70 bg-[#FBF5FD]/95 p-5 shadow-2xl sm:p-6"
      >
        <button data-testid="share-close-button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 text-[#7C6992] hover:text-[#7E57C2]">
          <X size={18} />
        </button>
        <p className="font-display text-2xl italic text-[#2A1B3D]">Share this feeling</p>
        <div className="aspect-[1080/1350] w-full max-w-[18rem] overflow-hidden rounded-2xl bg-white/60 shadow-[0_18px_40px_-20px_rgba(91,58,133,0.5)]">
          {card ? (
            <img data-testid="share-card-preview" src={card.url} alt={`Share card for ${painting.title}`} className="h-full w-full object-contain" />
          ) : (
            <div data-testid="share-card-loading" className="flex h-full items-center justify-center font-hand text-2xl text-[#7C6992]">
              {error || "painting your card…"}
            </div>
          )}
        </div>
        <input
          data-testid="share-name-input"
          value={name}
          maxLength={40}
          onChange={(e) => {
            setName(e.target.value);
            saveName(e.target.value.slice(0, 40));
          }}
          placeholder="from…"
          aria-label="Your name (optional)"
          className="font-hand w-full max-w-[18rem] border-0 border-b border-[#7E57C2]/20 bg-transparent px-1 py-1 text-center text-2xl text-[#4A3660] outline-none placeholder:text-[#B5A6C9] focus:border-[#7E57C2]/50"
        />
        <div className="flex flex-wrap justify-center gap-2">
          {canNative && (
            <button data-testid="share-native-button" onClick={native} className={quiet}>
              <Share2 size={13} /> Share
            </button>
          )}
          <button data-testid="share-download-button" disabled={!card} onClick={download} className={quiet}>
            <Download size={13} /> Download
          </button>
          <button data-testid="share-copy-link-button" onClick={copy} className={quiet}>
            <Link2 size={13} /> Copy link
          </button>
        </div>
        <p data-testid="share-status" role="status" className="min-h-[1.25rem] text-xs text-[#7E57C2]">
          {status}
        </p>
        <p data-testid="share-link-text" className="w-full truncate text-center text-xs text-[#5C4A72]">
          A link to “{painting.title}” in Feeling Heart
        </p>
      </motion.div>
    </motion.div>
  );
}

export function ShareControl({ painting, memory }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="share-open-button"
        onClick={() => setOpen(true)}
        className="group flex items-center gap-2 self-start font-display text-base text-[#5C4A72] transition-colors duration-300 hover:text-[#7E57C2]"
      >
        <Share2 size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5" /> Share
      </button>
      <AnimatePresence>{open && <ShareSheet painting={painting} memory={memory} onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
}
